import { createSignal, createEffect, createMemo, onCleanup, untrack, For, Show } from 'solid-js';
import { createStore, produce } from 'solid-js/store';
import { useNavigate, useParams, useSearchParams } from '@solidjs/router';
import Text from '../components/Text';
import SellbotNav from '../components/SellbotNav';
import HologramPreview from '../components/HologramPreview';
import HologramWindowPicker from '../components/HologramWindowPicker';
import HologramExclusionsCard from '../components/HologramExclusionsCard';
import SellbotOfferPicker from '../components/SellbotOfferPicker';
import SellbotOfferMultiPicker from '../components/SellbotOfferMultiPicker';
import { MAX_HOLOGRAM_LINES, lastRankStart, maxRepeatCount, usedHologramLines, withExcludedName } from '../models/HologramLayout';
import { SkeletonCard } from '../components/Skeleton';
import { useToast } from '../components/MessageBox';
import { useApi, isOk } from '../api';
import { firstError } from '../api/envelope';
import { useActiveBot } from '../state/activeBot';
import { useTranslation } from '../hooks/useTranslation';
import type { PlayerListDto, PlayerListSummaryDto, SellbotConfigDto, SellbotHologramBindingDto, SellbotHologramLineRequest, SellbotHologramListEntryDto } from '../api';
import sellbotStyles from './Sellbot.module.css';
import styles from './SellbotHolograms.module.css';

const RANK_SOURCES = ['OfferAnkauf', 'ShopAnkauf', 'ShopVerkauf', 'ShopGesamt'] as const;
const RANK_BYS = ['ItemCount', 'Amount', 'Trades'] as const;

const PREVIEW_DEBOUNCE_MS = 300;

interface DraftLine extends SellbotHologramLineRequest {
    worstCaseLength: number;
}

const emptyLine = (sortOrder: number): DraftLine => ({
    sortOrder, template: '', repeatCount: 1, fillerTemplate: '', worstCaseLength: 0,
    sellbotOfferId: null, window: null, rankSource: 'OfferAnkauf', rankBy: 'ItemCount', rankStart: 1,
    windowFrom: null, windowTo: null, windowLabelText: null, combinedOfferIds: [],
});

export default function SellbotHologram() {
    const api = useApi();
    const params = useParams();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const toast = useToast();
    const { t } = useTranslation();
    const { botId } = useActiveBot();

    const isNew = () => params.hologramId === 'new';

    const [loading, setLoading] = createSignal(true);
    const [saving, setSaving] = createSignal(false);
    const [previewing, setPreviewing] = createSignal(false);
    const [config, setConfig] = createSignal<SellbotConfigDto | null>(null);
    const [bindings, setBindings] = createSignal<SellbotHologramBindingDto[]>([]);
    const [live, setLive] = createSignal<SellbotHologramListEntryDto[]>([]);
    const [savedClaim, setSavedClaim] = createSignal<number | null>(null);
    const [preview, setPreview] = createSignal<string[]>([]);
    const [budget, setBudget] = createSignal(84);
    const [focusedLine, setFocusedLine] = createSignal(0);
    const [background, setBackground] = createSignal<'day' | 'night'>('day');

    const [draft, setDraft] = createStore({
        title: '',
        pholoName: Number(searchParams.name ?? 1),
        isEnabled: true,
        sellbotOfferId: null as string | null,
        ankaufPriceStageId: null as string | null,
        combinedOfferIds: [] as string[],
        window: 'AllTime' as string,
        windowFrom: null as string | null,
        windowTo: null as string | null,
        windowLabelText: null as string | null,
        lines: [emptyLine(0)] as DraftLine[],
        excludedPlayers: [] as string[],
        excludedLists: [] as PlayerListSummaryDto[],
    });

    const load = async () => {
        const id = botId();
        if (!id) { setLoading(false); return; }

        const [cfg, catalog, inGame] = await Promise.all([
            api.sellbot.getConfig(id),
            api.sellbotHologram.bindings(id),
            api.sellbotHologram.live(id),
        ]);
        if (isOk(cfg.data)) setConfig(cfg.data.value);
        if (isOk(catalog.data)) setBindings(catalog.data.value.bindings);
        if (isOk(inGame.data)) setLive(inGame.data.value.entries);

        if (!isNew()) {
            const res = await api.sellbotHologram.list(id);
            const hologram = isOk(res.data) ? res.data.value.find(h => h.id === params.hologramId) : undefined;
            if (!hologram) { toast?.error(t('HOLO_TOAST_NOT_FOUND')); navigate('/sellbot/holograms'); return; }
            setSavedClaim(hologram.pholoName);
            setDraft({
                title: hologram.title,
                pholoName: hologram.pholoName,
                isEnabled: hologram.isEnabled,
                sellbotOfferId: hologram.sellbotOfferId ?? null,
                ankaufPriceStageId: hologram.ankaufPriceStageId ?? null,
                combinedOfferIds: [...hologram.combinedOfferIds],
                window: hologram.window,
                windowFrom: hologram.windowFrom ?? null,
                windowTo: hologram.windowTo ?? null,
                windowLabelText: hologram.windowLabelText ?? null,
                excludedPlayers: hologram.exclusions.players.map(p => p.playerName),
                excludedLists: hologram.exclusions.lists,
                lines: hologram.lines.map(l => ({
                    sortOrder: l.sortOrder,
                    template: l.template,
                    repeatCount: l.repeatCount,
                    fillerTemplate: l.fillerTemplate,
                    worstCaseLength: l.worstCaseLength,
                    sellbotOfferId: l.sellbotOfferId ?? null,
                    window: l.window ?? null,
                    rankSource: l.rankSource,
                    rankBy: l.rankBy,
                    rankStart: l.rankStart,
                    windowFrom: l.windowFrom ?? null,
                    windowTo: l.windowTo ?? null,
                    windowLabelText: l.windowLabelText ?? null,
                    combinedOfferIds: [...l.combinedOfferIds],
                })),
            });
        }

        // ?name carries the hologram the operator clicked on the list page; without it, or when that
        // one turns out to be taken, fall back to the first free hologram.
        if (!selectableHolograms().some(entry => entry.pholoName === draft.pholoName))
            setDraft('pholoName', selectableHolograms()[0]?.pholoName ?? draft.pholoName);

        setLoading(false);
    };

    createEffect(() => {
        botId();
        setLoading(true);
        untrack(() => { void load(); });
    });

    const selectedOffer = createMemo(() =>
        config()?.offers.find(o => o.id === draft.sellbotOfferId));

    const stages = createMemo(() => selectedOffer()?.ankaufTier?.stages ?? []);

    // A layout can only claim a hologram that is already standing in game and that no other layout
    // holds. The exemption keys on the saved claim, not on the draft, or picking a taken number in
    // the dropdown would make that number look available.
    const selectableHolograms = createMemo(() =>
        live().filter(entry => !entry.isClaimed || entry.pholoName === savedClaim()));

    const claimIsSelectable = createMemo(() =>
        selectableHolograms().some(entry => entry.pholoName === draft.pholoName));

    const exclusionFields = () => ({
        excludedPlayers: [...draft.excludedPlayers],
        excludedPlayerListIds: draft.excludedLists.map(l => l.id),
    });

    const combinedOfferFields = () => ({
        combinedOfferIds: draft.sellbotOfferId ? [...draft.combinedOfferIds] : [],
    });

    const chooseOffer = (offerId: string | null) => {
        setDraft('sellbotOfferId', offerId);
        setDraft('ankaufPriceStageId', null);
        setDraft('combinedOfferIds', ids => ids.filter(id => id !== offerId));
    };

    const usedLines = createMemo(() => usedHologramLines(draft.lines));

    const renderPreview = async () => {
        const id = botId();
        if (!id) return;
        setPreviewing(true);
        const res = await api.sellbotHologram.render(id, {
            pholoName: draft.pholoName,
            sellbotOfferId: draft.sellbotOfferId,
            ankaufPriceStageId: draft.ankaufPriceStageId,
            window: draft.window,
            windowFrom: draft.windowFrom,
            windowTo: draft.windowTo,
            windowLabelText: draft.windowLabelText,
            lines: draft.lines.map(({ worstCaseLength: _ignored, ...line }) => line),
            ...exclusionFields(),
            ...combinedOfferFields(),
        });
        setPreviewing(false);
        if (!isOk(res.data)) return;
        setPreview(res.data.value.lines);
        setBudget(res.data.value.budget);
    };

    createEffect(() => {
        // Touch every field the render depends on so a keystroke schedules a fresh preview. A field
        // missing from this list leaves the preview showing something the bot would not send.
        draft.lines.forEach(l =>
            [l.template, l.repeatCount, l.fillerTemplate, l.sellbotOfferId, l.window, l.rankSource, l.rankBy,
                l.rankStart, l.windowFrom, l.windowTo, l.windowLabelText, l.combinedOfferIds?.join()]);
        draft.pholoName;
        draft.sellbotOfferId;
        draft.ankaufPriceStageId;
        draft.combinedOfferIds.join();
        draft.window;
        draft.windowFrom;
        draft.windowTo;
        draft.windowLabelText;
        draft.excludedPlayers.join();
        draft.excludedLists.map(l => l.id).join();

        const timer = setTimeout(() => void renderPreview(), PREVIEW_DEBOUNCE_MS);
        onCleanup(() => clearTimeout(timer));
    });

    const save = async () => {
        const id = botId();
        if (!id) return;
        setSaving(true);
        const body = {
            title: draft.title,
            pholoName: draft.pholoName,
            isEnabled: draft.isEnabled,
            sellbotOfferId: draft.sellbotOfferId,
            ankaufPriceStageId: draft.ankaufPriceStageId,
            window: draft.window,
            windowFrom: draft.windowFrom,
            windowTo: draft.windowTo,
            windowLabelText: draft.windowLabelText,
            lines: draft.lines.map(({ worstCaseLength: _ignored, ...line }) => line),
            ...exclusionFields(),
            ...combinedOfferFields(),
        };
        const res = isNew()
            ? await api.sellbotHologram.create(id, body)
            : await api.sellbotHologram.update(id, params.hologramId, body);
        setSaving(false);

        if (!isOk(res.data)) {
            toast?.error(firstError(res.data) ?? t('HOLO_TOAST_SAVE_FAILED'));
            return;
        }
        toast?.success(t('HOLO_TOAST_SAVED'));
        navigate('/sellbot/holograms');
    };

    const excludePlayer = (name: string) => setDraft('excludedPlayers', players => [...withExcludedName(players, name)]);

    const excludeList = (list: PlayerListDto) => {
        if (draft.excludedLists.some(existing => existing.id === list.id)) return;
        setDraft('excludedLists', lists => [...lists, { id: list.id, name: list.name, memberCount: list.memberCount }]);
    };

    const addLine = () => setDraft('lines', produce((lines: DraftLine[]) => { lines.push(emptyLine(lines.length)); }));

    const removeLine = (index: number) => setDraft('lines', produce((lines: DraftLine[]) => {
        lines.splice(index, 1);
        lines.forEach((line, position) => { line.sortOrder = position; });
    }));

    const moveLine = (index: number, delta: number) => setDraft('lines', produce((lines: DraftLine[]) => {
        const target = index + delta;
        if (target < 0 || target >= lines.length) return;
        [lines[index], lines[target]] = [lines[target], lines[index]];
        lines.forEach((line, position) => { line.sortOrder = position; });
    }));

    const chooseLineOffer = (index: number, offerId: string | null) => {
        setDraft('lines', index, 'sellbotOfferId', offerId);
        setDraft('lines', index, 'combinedOfferIds', ids => offerId ? (ids ?? []).filter(id => id !== offerId) : []);
    };

    const insertBinding = (token: string) => {
        const index = focusedLine();
        if (index < 0 || index >= draft.lines.length) return;
        setDraft('lines', index, 'template', current => `${current}{${token}}`);
    };

    const effectiveOffer = (line: DraftLine | undefined) =>
        line?.sellbotOfferId ?? draft.sellbotOfferId;

    // Offer bindings need an offer, and so do rank bindings while the line ranks a single offer
    // rather than the whole shop.
    const bindingDisabled = (binding: SellbotHologramBindingDto) => {
        const line = draft.lines[focusedLine()];
        if (binding.needsOffer) return !effectiveOffer(line);
        return binding.category === 'Rank'
            && (line?.rankSource ?? 'OfferAnkauf') === 'OfferAnkauf'
            && !effectiveOffer(line);
    };

    // Grouped in the order the backend lists them, so related values sit together instead of one
    // undifferentiated wall of tokens.
    const bindingGroups = createMemo(() => {
        const groups: { category: string; bindings: SellbotHologramBindingDto[] }[] = [];
        for (const binding of bindings()) {
            const existing = groups.find(g => g.category === binding.category);
            if (existing) existing.bindings.push(binding);
            else groups.push({ category: binding.category, bindings: [binding] });
        }
        return groups;
    });

    const lengthClass = (line: DraftLine) => line.worstCaseLength > budget() ? styles.lengthOver : styles.lengthOk;

    return (
        <div class={sellbotStyles.container}>
            <SellbotNav />

            <div class={sellbotStyles.headerRow}>
                <div class="v" style={{ gap: '4px' }}>
                    <h1><Text val={isNew() ? 'HOLO_EDITOR_NEW_TITLE' : 'HOLO_EDITOR_TITLE'} /></h1>
                    <span class={sellbotStyles.heroSubtitle}><Text val="HOLO_EDITOR_SUBTITLE" /></span>
                </div>
                <div class="h" style={{ gap: 'var(--spacing-sm)' }}>
                    <button class="btn btn-secondary" onClick={() => navigate("/sellbot/holograms")}>
                        <Text val="HOLO_CANCEL" />
                    </button>
                    <button class="btn btn-primary" onClick={() => void save()}
                        disabled={saving() || loading() || !claimIsSelectable() || usedLines() > MAX_HOLOGRAM_LINES}>
                        <i class="fa-solid fa-floppy-disk" />
                        <Text val="HOLO_SAVE" />
                    </button>
                </div>
            </div>

            <Show when={!loading()} fallback={<div class="v" style={{ gap: 'var(--spacing-md)' }}><SkeletonCard /><SkeletonCard /></div>}>
                <div class={styles.editorLayout}>
                    <div class="v" style={{ gap: 'var(--spacing-md)' }}>
                        <div class="fcard v" style={{ gap: 'var(--spacing-md)' }}>
                            <h2 class={styles.sectionTitle} style={{ 'margin-top': '0' }}><Text val="HOLO_SCOPE" /></h2>

                            <label class="v" style={{ gap: '4px' }}>
                                <Text val="HOLO_FIELD_TITLE" />
                                <input type="text" value={draft.title} onInput={(e) => setDraft('title', e.currentTarget.value)} />
                            </label>

                            <div class="h" style={{ gap: 'var(--spacing-md)', 'flex-wrap': 'wrap' }}>
                                <label class="v" style={{ gap: '4px' }}>
                                    <Text val="HOLO_FIELD_PHOLO_NAME" />
                                    <select value={String(draft.pholoName)}
                                        onChange={(e) => setDraft('pholoName', Number(e.currentTarget.value))}>
                                        <For each={selectableHolograms()}>
                                            {(entry) => (
                                                <option value={String(entry.pholoName)}>
                                                    {entry.pholoName} ({entry.x} {entry.y} {entry.z})
                                                </option>
                                            )}
                                        </For>
                                    </select>
                                    <Show when={!claimIsSelectable()}>
                                        <span class={styles.fieldError}><Text val="HOLO_NO_PLACED_HOLOGRAM" /></span>
                                    </Show>
                                </label>

                                <HologramWindowPicker value={draft}
                                    onChange={(patch) => setDraft({ ...patch, window: patch.window ?? draft.window })} />

                                <label class="h hcenter" style={{ gap: 'var(--spacing-xs)', 'align-self': 'flex-end' }}>
                                    <input type="checkbox" checked={draft.isEnabled}
                                        onChange={(e) => setDraft('isEnabled', e.currentTarget.checked)} />
                                    <Text val="HOLO_FIELD_ENABLED" />
                                </label>
                            </div>

                            <div class="v" style={{ gap: '4px' }}>
                                <Text val="HOLO_FIELD_OFFER" />
                                <SellbotOfferPicker
                                    offers={config()?.offers ?? []}
                                    value={draft.sellbotOfferId ?? ''}
                                    onChange={(offerId) => chooseOffer(offerId || null)}
                                    placeholder={t('HOLO_OFFER_NONE')}
                                    emptyOption={{ label: t('HOLO_OFFER_NONE'), icon: 'fa-ban' }} />
                            </div>

                            <Show when={draft.sellbotOfferId}>
                                {(mainOfferId) => (
                                    <div class="v" style={{ gap: '4px' }}>
                                        <Text val="HOLO_FIELD_COMBINED_OFFERS" />
                                        <SellbotOfferMultiPicker
                                            offers={config()?.offers ?? []}
                                            value={draft.combinedOfferIds}
                                            excludedOfferIds={[mainOfferId()]}
                                            onChange={(offerIds) => setDraft('combinedOfferIds', offerIds)}
                                            placeholder={t('HOLO_COMBINED_OFFERS_ADD')} />
                                        <span class={styles.sectionHint}><Text val="HOLO_COMBINED_OFFERS_HINT" /></span>
                                    </div>
                                )}
                            </Show>

                            <Show when={stages().length > 0}>
                                <label class="v" style={{ gap: '4px' }}>
                                    <Text val="HOLO_FIELD_STAGE" />
                                    <select value={draft.ankaufPriceStageId ?? ''}
                                        onChange={(e) => setDraft('ankaufPriceStageId', e.currentTarget.value || null)}>
                                        <option value="">{t('HOLO_STAGE_LARGEST')}</option>
                                        <For each={stages()}>
                                            {(stage) => <option value={stage.id}>{stage.count}x · {stage.price}$</option>}
                                        </For>
                                    </select>
                                </label>
                            </Show>
                        </div>

                        <div class="fcard v" style={{ gap: 'var(--spacing-md)' }}>
                            <div class="h hcenter" style={{ 'justify-content': 'space-between' }}>
                                <h2 class={styles.sectionTitle} style={{ 'margin': '0' }}><Text val="HOLO_LINES" /></h2>
                                <button class="btn btn-secondary" onClick={addLine} disabled={usedLines() >= MAX_HOLOGRAM_LINES}>
                                    <i class="fa-solid fa-plus" />
                                    <Text val="HOLO_ADD_LINE" />
                                </button>
                            </div>

                            <For each={draft.lines}>
                                {(line, index) => (
                                    <div class={styles.lineRow}>
                                        <span class={styles.lineIndex}>{index() + 1}</span>

                                        <div class="v" style={{ gap: '4px' }}>
                                            <input type="text" class="minecraft-text" value={line.template}
                                                placeholder={t('HOLO_LINE_PLACEHOLDER')}
                                                onFocus={() => setFocusedLine(index())}
                                                onInput={(e) => setDraft('lines', index(), 'template', e.currentTarget.value)} />

                                            <div class={styles.lineMeta}>
                                                <label class="h hcenter" style={{ gap: '4px' }}>
                                                    <Text val="HOLO_LINE_REPEAT" />
                                                    <input class={styles.repeatField} type="number" min="1"
                                                        max={maxRepeatCount(draft.lines, index())} value={line.repeatCount}
                                                        onInput={(e) => setDraft('lines', index(), 'repeatCount', Math.max(1, Number(e.currentTarget.value) || 1))} />
                                                </label>
                                                <span class={lengthClass(line)}>
                                                    <Text val="HOLO_LINE_WORST_CASE" /> {line.worstCaseLength} / {budget()}
                                                </span>
                                            </div>

                                            <div class={styles.lineScope}>
                                                <div class={`v ${styles.lineOffer}`} style={{ gap: '2px' }}>
                                                    <Text val="HOLO_LINE_OFFER" />
                                                    <SellbotOfferPicker
                                                        offers={config()?.offers ?? []}
                                                        value={line.sellbotOfferId ?? ''}
                                                        onChange={(offerId) => chooseLineOffer(index(), offerId || null)}
                                                        placeholder={t('HOLO_LINE_INHERIT')}
                                                        emptyOption={{ label: t('HOLO_LINE_INHERIT'), icon: 'fa-arrow-turn-up' }} />
                                                </div>

                                                <Show when={line.sellbotOfferId}>
                                                    {(lineOfferId) => (
                                                        <div class={styles.lineCombined}>
                                                            <Text val="HOLO_FIELD_COMBINED_OFFERS" />
                                                            <SellbotOfferMultiPicker
                                                                offers={config()?.offers ?? []}
                                                                value={line.combinedOfferIds ?? []}
                                                                excludedOfferIds={[lineOfferId()]}
                                                                onChange={(offerIds) => setDraft('lines', index(), 'combinedOfferIds', offerIds)}
                                                                placeholder={t('HOLO_COMBINED_OFFERS_ADD')} />
                                                        </div>
                                                    )}
                                                </Show>

                                                <HologramWindowPicker value={line} allowInherit dense
                                                    onChange={(patch) => setDraft('lines', index(), patch)} />

                                                <Show when={line.repeatCount > 1}>
                                                    <label class="v" style={{ gap: '2px' }}>
                                                        <Text val="HOLO_LINE_SOURCE" />
                                                        <select value={line.rankSource ?? 'OfferAnkauf'}
                                                            onChange={(e) => setDraft('lines', index(), 'rankSource', e.currentTarget.value)}>
                                                            <For each={RANK_SOURCES}>
                                                                {(source) => <option value={source}>{t('HOLO_LINE_SOURCE_' + source.toUpperCase())}</option>}
                                                            </For>
                                                        </select>
                                                    </label>

                                                    <label class="v" style={{ gap: '2px' }}>
                                                        <Text val="HOLO_LINE_RANKBY" />
                                                        <select value={line.rankBy ?? 'ItemCount'}
                                                            onChange={(e) => setDraft('lines', index(), 'rankBy', e.currentTarget.value)}>
                                                            <For each={RANK_BYS}>
                                                                {(by) => <option value={by}>{t('HOLO_LINE_RANKBY_' + by.toUpperCase())}</option>}
                                                            </For>
                                                        </select>
                                                    </label>

                                                    <label class="v" style={{ gap: '2px' }}>
                                                        <Text val="HOLO_LINE_RANK_START" />
                                                        <input class={styles.repeatField} type="number" min="1"
                                                            max={lastRankStart(line.repeatCount)} value={line.rankStart ?? 1}
                                                            onInput={(e) => setDraft('lines', index(), 'rankStart', Math.max(1, Number(e.currentTarget.value) || 1))} />
                                                    </label>
                                                </Show>
                                            </div>

                                            <Show when={line.repeatCount > 1}>
                                                <input type="text" class="minecraft-text" value={line.fillerTemplate}
                                                    placeholder={t('HOLO_LINE_FILLER_PLACEHOLDER')}
                                                    onFocus={() => setFocusedLine(index())}
                                                    onInput={(e) => setDraft('lines', index(), 'fillerTemplate', e.currentTarget.value)} />
                                            </Show>
                                        </div>

                                        <div class="h" style={{ gap: '2px' }}>
                                            <button class="btn btn-icon btn-sm" title={t('HOLO_LINE_UP')} onClick={() => moveLine(index(), -1)}>
                                                <i class="fa-solid fa-arrow-up" />
                                            </button>
                                            <button class="btn btn-icon btn-sm" title={t('HOLO_LINE_DOWN')} onClick={() => moveLine(index(), 1)}>
                                                <i class="fa-solid fa-arrow-down" />
                                            </button>
                                            <button class="btn btn-icon btn-sm btn-danger" title={t('HOLO_LINE_REMOVE')} onClick={() => removeLine(index())}>
                                                <i class="fa-solid fa-trash" />
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </For>

                            <p class={usedLines() > MAX_HOLOGRAM_LINES ? styles.fieldError : styles.sectionHint}>
                                {t('HOLO_LINE_LIMIT', MAX_HOLOGRAM_LINES, usedLines())}
                            </p>
                            <p class={styles.sectionHint}><Text val="HOLO_SHRINK_HINT" /></p>
                        </div>

                        <HologramExclusionsCard
                            titleKey="HOLO_EXCLUDE_HOLOGRAM_TITLE" helpKey="HOLO_EXCLUDE_HOLOGRAM_HELP"
                            players={draft.excludedPlayers.map(name => ({ id: name, playerName: name }))}
                            lists={draft.excludedLists}
                            onAddPlayer={excludePlayer}
                            onRemovePlayer={(player) => setDraft('excludedPlayers', players => players.filter(name => name !== player.playerName))}
                            onAttachList={excludeList}
                            onDetachList={(playerListId) => setDraft('excludedLists', lists => lists.filter(l => l.id !== playerListId))} />

                        <div class="fcard v" style={{ gap: 'var(--spacing-sm)' }}>
                            <h2 class={styles.sectionTitle} style={{ 'margin': '0' }}><Text val="HOLO_BINDINGS" /></h2>
                            <p class={styles.sectionHint}><Text val="HOLO_BINDINGS_HINT" /></p>
                            <For each={bindingGroups()}>
                                {(group) => (
                                    <div class="v" style={{ gap: 'var(--spacing-xs)' }}>
                                        <span class={styles.bindingGroupTitle}>
                                            <Text val={'HOLO_BIND_GROUP_' + group.category.toUpperCase()} />
                                        </span>
                                        <For each={group.bindings}>
                                            {(binding) => (
                                                <button class={`btn btn-sm ${styles.bindingRow}`}
                                                    disabled={bindingDisabled(binding)}
                                                    onClick={() => insertBinding(binding.token)}>
                                                    <code class={styles.bindingToken}>{'{' + binding.token + '}'}</code>
                                                    <span class={styles.bindingDescription}>{t(binding.labelKey)}</span>
                                                </button>
                                            )}
                                        </For>
                                    </div>
                                )}
                            </For>
                        </div>
                    </div>

                    <div class={`fcard v ${styles.previewColumn}`}>
                        <div class="h hcenter" style={{ 'justify-content': 'space-between' }}>
                            <h2 class={styles.sectionTitle} style={{ 'margin': '0' }}><Text val="HOLO_PREVIEW" /></h2>
                            <select value={background()} onChange={(e) => setBackground(e.currentTarget.value as 'day' | 'night')}>
                                <option value="day">{t('HOLO_PREVIEW_DAY')}</option>
                                <option value="night">{t('HOLO_PREVIEW_NIGHT')}</option>
                            </select>
                        </div>

                        <HologramPreview lines={preview()} background={background()} loading={previewing()} />

                        <p class={styles.sectionHint}><Text val="HOLO_PREVIEW_HINT" /></p>
                    </div>
                </div>
            </Show>
        </div>
    );
}
